import { type FC } from "react";

import { Control, Tag, TagContainer } from "@snapchat/snap-design-system";

import { type RemoteApiSpec } from "../../../../../gql-generated/types";
import { TagsInput } from "../../../../../shared/formik/tags-input";
import {
    CustomProcessorLabel,
    CustomVisibilityUserListButtonCta,
    CustomVisibilityUserListLabel,
    CustomVisibilityUserListTooltip,
    DescriptionLabel,
    DescriptionTooltip,
    DocumentationUrlLabel,
    DocumentationUrlReadTooltip,
    HostLabel,
    HostTooltip,
    IconUrlLabel,
    IconUrlReadTooltip,
    MaxRequestSizeLabel,
    MaxRequestSizeTootip,
    MaxResponseSizeLabel,
    MaxResponseSizeTooltip,
    MaxResponseTimeLabel,
    MinLensStudioVersionLabel,
    MinLensStudioVersionTooltip,
    NameLabel,
    NameTooltip,
    ProcessorLabel,
    ProvidedProcessorLabel,
    ProviderLabel,
    ProviderTooltip,
    SnapkitAppIdLabel,
    SnapkitAppIdTooltip,
    TargetPlatformsLabel,
    TargetPlatformsReadTooltip,
    VisibilityLabel,
    VisibilityTooltip,
} from "../../../messages.generated";
import { NotApplicableText } from "../../messages.generated";

import { Divider, controlStyle, iconPreview } from "./ApiSettingsCard.styles";

interface ApiSettingsCardDetailsProps {
    spec: RemoteApiSpec;
}

export const ApiSettingsCardDetails: FC<ApiSettingsCardDetailsProps> = ({ spec }) => {
    const {
        name,
        providerName,
        documentationUrl,
        description,
        iconUrl,
        targetPlatforms,
        visibility,
        customVisibilityUsersList = [],
        minLensStudioVersion,
        snapkitAppId,
        useBuiltinLocalProcessor,
        host,
        maxRequestSizeBytes,
        maxResponseSizeBytes,
        maxResponseTimeMillis,
    } = spec;

    const userList = customVisibilityUsersList?.filter((list) => !!list) ?? [];
    return (
        <>
            <Control label={<NameLabel />} className={controlStyle} tooltip={<NameTooltip />}>
                {name}
            </Control>

            <Control label={<ProviderLabel />} className={controlStyle} tooltip={<ProviderTooltip />}>
                {providerName}
            </Control>
            <Control
                label={<DocumentationUrlLabel />}
                className={controlStyle}
                tooltip={<DocumentationUrlReadTooltip />}
            >
                <a href={documentationUrl}>{documentationUrl}</a>
            </Control>
            <Control label={<DescriptionLabel />} className={controlStyle} tooltip={<DescriptionTooltip />}>
                {description}
            </Control>
            <Control label={<IconUrlLabel />} className={controlStyle} tooltip={<IconUrlReadTooltip />}>
                {iconUrl}
            </Control>
            <img className={iconPreview} src={iconUrl} alt="" />

            <Control label={<TargetPlatformsLabel />} className={controlStyle} tooltip={<TargetPlatformsReadTooltip />}>
                {targetPlatforms.length > 0 ? (
                    <TagContainer>
                        {targetPlatforms.map((platform) => (
                            <Tag key={platform}>{platform}</Tag>
                        ))}
                    </TagContainer>
                ) : (
                    <NotApplicableText />
                )}
            </Control>

            <Control label={<VisibilityLabel />} className={controlStyle} tooltip={<VisibilityTooltip />}>
                {visibility}
            </Control>

            <Control
                label={<MinLensStudioVersionLabel />}
                className={controlStyle}
                tooltip={<MinLensStudioVersionTooltip />}
            >
                {minLensStudioVersion}
            </Control>

            <Divider />

            <TagsInput
                label={<CustomVisibilityUserListLabel />}
                tooltip={<CustomVisibilityUserListTooltip />}
                items={userList}
                buttonCta={<CustomVisibilityUserListButtonCta />}
                readOnly={true}
            />
            <Control label={<SnapkitAppIdLabel />} tooltip={<SnapkitAppIdTooltip />} className={controlStyle}>
                {snapkitAppId}
            </Control>
            <Control label={<ProcessorLabel />} className={controlStyle}>
                {useBuiltinLocalProcessor ? <ProvidedProcessorLabel /> : <CustomProcessorLabel />}
            </Control>

            <Control label={<HostLabel />} className={controlStyle} tooltip={<HostTooltip />}>
                {host}
            </Control>

            <Control label={<MaxRequestSizeLabel />} className={controlStyle} tooltip={<MaxRequestSizeTootip />}>
                {maxRequestSizeBytes}
            </Control>
            <Control label={<MaxResponseSizeLabel />} className={controlStyle} tooltip={<MaxResponseSizeTooltip />}>
                {maxResponseSizeBytes}
            </Control>
            <Control label={<MaxResponseTimeLabel />} className={controlStyle} tooltip={<MaxResponseSizeTooltip />}>
                {maxResponseTimeMillis}
            </Control>
        </>
    );
};
